import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import axios from 'axios';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [addressData, setAddressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState(null);
  const [customerResale, setCustomerResale] = useState(null);
  const [customerResaleLoading, setCustomerResaleLoading] = useState(false);

  // Axios response interceptor to catch 401 errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && error.config?._retry) {
          console.warn('[Auth] Received 401 after retry – ending session.');
          handleSessionTimeout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Listen for session expiration events from authService
  useEffect(() => {
    const handleSessionExpired = (event) => {
      console.warn('[Auth] Session expired event received:', event.detail);
      handleSessionTimeout();
    };

    window.addEventListener('sessionExpired', handleSessionExpired);

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, []);

  const handleSessionTimeout = () => {
    console.warn('[Auth] Session expired or invalid.');
    setIsAuthenticated(false);
    setAddressData(null);
    setUserEmail(null);
    setCustomerResale(null);
    setLoading(false);
  };

  const getAddressData = async () => {
    try {
      const result = await authService.getAddressData();
      if (result.success) {
        setAddressData(result.data?.address);
        return result.data?.address;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  const checkAuth = async () => {
    try {
      setLoading(true);

      const verifyResponse = await authService.verifyAuth();
      const success = verifyResponse.success || verifyResponse.data?.success;
      const message = verifyResponse.message || verifyResponse.data?.message;

      if (!success || message === 'User not logged in') {
        setIsAuthenticated(false);
        setAddressData(null);
        setUserEmail(null);
        handleSessionTimeout();
        return;
      }

      // ✅ Auth is valid
      setIsAuthenticated(true);

      // Extract user email from response if available
      if (verifyResponse.data?.email) {
        setUserEmail(verifyResponse.data.email);
      }

      try {
        const address = await getAddressData();
        setAddressData(address || null);
      } catch (err) {
        console.warn('[Auth] Failed to fetch address after auth check:', err);
        setAddressData(null);
      }
    } catch (error) {
      console.error('[Auth] checkAuth error:', error.message || error);
      handleSessionTimeout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Load customer resale data when address data changes
  useEffect(() => {
    const loadCustomerResaleData = async () => {
      if (!isAuthenticated || !addressData?.labAccountNumber) {
        setCustomerResale(null);
        return;
      }

      setCustomerResaleLoading(true);
      try {
        const response = await fetch(
          `/api/customer/${addressData.labAccountNumber}`
        );
        const result = await response.json();
        if (result.success) {
          setCustomerResale(result.data);
        } else {
          setCustomerResale('error');
        }
      } catch (error) {
        console.error('[Auth] Failed to fetch customer resale data:', error);
        setCustomerResale(null);
      } finally {
        setCustomerResaleLoading(false);
      }
    };

    loadCustomerResaleData();
  }, [isAuthenticated, addressData]);

  const requestCode = async (email) => {
    try {
      const result = await authService.requestCode(email);
      return result;
    } catch (error) {
      return { success: false, error };
    }
  };

  const verifyCode = async (email, code) => {
    try {
      const result = await authService.verifyCode(email, code);
      if (result.success) {
        setIsAuthenticated(true);

        // Extract user email from response if available
        if (result.data?.email) {
          setUserEmail(result.data.email);
        }

        // Fetch address data after successful authentication
        try {
          const address = await getAddressData();
          setAddressData(address || null);
        } catch (err) {
          console.warn(
            '[Auth] Address fetch failed after code verification:',
            err
          );
          setAddressData(null);
        }
      }
      return result;
    } catch (error) {
      console.error('[Auth] Code verification error:', error);
      return { success: false, error };
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      const result = await authService.logout();
      if (result.success) {
        setIsAuthenticated(false);
        setAddressData(null);
        setUserEmail(null);
        setCustomerResale(null);
      } else {
        console.warn('[Auth] Logout error:', result.message || result);
      }
      return result;
    } catch (error) {
      console.error('[Auth] Logout error:', error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  const updateEmail = async (newEmail) => {
    try {
      const result = await authService.updateEmail(newEmail);
      return result;
    } catch (error) {
      console.error('[Auth] Email update error:', error);
      return { success: false, error };
    }
  };

  const verifyEmailChange = async (token) => {
    try {
      const result = await authService.verifyEmailChange(token);
      if (result.success) {
        // Update user email if verification successful
        if (result.data?.email) {
          setUserEmail(result.data.email);
        }
      }
      return result;
    } catch (error) {
      console.error('[Auth] Email change verification error:', error);
      return { success: false, error };
    }
  };

  const verifyEmailChangeCode = async (code) => {
    try {
      const result = await authService.verifyEmailChangeCode(code);
      if (result.success) {
        // Update user email if verification successful
        if (result.data?.email) {
          setUserEmail(result.data.email);
        }
      }
      return result;
    } catch (error) {
      console.error('[Auth] Email change code verification error:', error);
      return { success: false, error };
    }
  };

  const updateAddress = async (addressData) => {
    try {
      const result = await authService.updateAddress(addressData);
      if (result.success) {
        const data = await getAddressData();
        setAddressData(data);
        // Customer resale data will be reloaded automatically via useEffect
        return { success: true };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to update address',
        };
      }
    } catch (error) {
      console.error('[Auth] Address update error:', error);
      return { success: false, message: error.message || 'An error occurred' };
    }
  };

  const deleteAccount = async () => {
    try {
      const result = await authService.deleteAccount();
      if (result.success) {
        // Clear all auth state
        setIsAuthenticated(false);
        setAddressData(null);
        setUserEmail(null);
        setCustomerResale(null);
        setLoading(false);
      }
      return result;
    } catch (error) {
      console.error('[Auth] Delete account error:', error);
      return { success: false, message: error.message || 'An error occurred' };
    }
  };

  const isProfileComplete = () => {
    const hasAddress =
      addressData &&
      addressData.firstName &&
      addressData.lastName &&
      addressData.phone &&
      addressData.address1 &&
      addressData.city &&
      addressData.state &&
      addressData.zipCode;

    return hasAddress;
  };

  const value = {
    isAuthenticated,
    addressData,
    userEmail,
    loading,
    customerResale,
    customerResaleLoading,
    requestCode,
    verifyCode,
    logout,
    checkAuth,
    updateEmail,
    verifyEmailChange,
    verifyEmailChangeCode,
    updateAddress,
    getAddressData,
    deleteAccount,
    isProfileComplete,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
